import { use, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import { EmptyState } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import ExportForm, {
	getExportFormatExtension,
	type ExportFormat,
	type ExportStatus,
} from '~components/forms/exportForm';
import { generalInformation, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { Key, KeysDataGrid } from '~features/keys';
import KeyMultiAssignDialog from '~features/keys/components/dialogs/keyMultiAssignDialogs';
import KeyMultiUnassignDialog from '~features/keys/components/dialogs/keyMultiUnassignDialogs';
import SelectedKeysContext from '~features/keys/providers/selectedKeysContext';
import { ResourceGroupService } from '~features/resourceGroups';
import { useSnackbar } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import ExportService from '~services/exportService';
import { getService } from '~services/serviceFactory';
import { triggerBlobDownload } from '~utils/fileUtils';

import OrganisationActionBar from '../components/organisationActionBar';
import OrganisationContext from '../providers/organisationDetailsContext';
import OrganisationsService from '../services/organisationsService';

type UserAction = {
	type: 'assignKeys' | 'unAssignKeys' | 'export' | 'changeResourceGroupForKeys';
	mutate?: KeyedMutator<OffsetPagedResults<Key>>;
	clearSelection?: () => void;
} | null;

const organisationService = getService(OrganisationsService);
const resourceGroupService = getService(ResourceGroupService);
const exportService = getService(ExportService);

const OrganisationKeysContainer = () => {
	const { t } = useTranslation();
	const { enqueueSnackbar, enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();
	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const [exportStatus, setExportStatus] = useState<ExportStatus>('selecting');
	const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
	const [showModalLoader, setShowModalLoader] = useState(false);
	const [searchParams] = useSearchParams();
	const organisationContext = use(OrganisationContext);
	const selectedKeysContext = use(SelectedKeysContext);
	const organisationId = organisationContext.organisation?.id ?? '0';
	const allowsExporting = useAuthorize().authorize([
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.Support,
		UserRoleEnum.Manufacturing,
		UserRoleEnum.ClientAdmin,
	]);

	const clearUserAction = () => {
		setUserAction(null);
		selectedKeysContext.updateSingleKey(undefined);
		setTimeout(() => {
			setExportStatus('selecting');
			setSelectedFormat(null);
		}, 300);
	};

	const handleAssign = (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'assignKeys',
			mutate: mutate,
			clearSelection,
		});
	};

	const handleUnassign = (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'unAssignKeys',
			mutate: mutate,
			clearSelection: clearSelection,
		});
	};

	const handleChangeResourceGroupForKeys = (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'changeResourceGroupForKeys',
			mutate: mutate,
			clearSelection,
		});
	};

	const handleUnassignKeys = async () => {
		const keyIds =
			selectedKeysContext?.singleKey ?
				[selectedKeysContext?.singleKey.id]
			:	(selectedKeysContext?.selectedKeys?.flatMap((value) => value.id) ?? []);
		const isSingleKey = keyIds.length == 1;
		const successValue =
			isSingleKey ?
				t('keyUnassignedSuccesfully', { ns: 'toast' })
			:	t('keysUnassignedSuccesfully', { ns: 'toast' });
		const failedValue =
			isSingleKey ?
				t('keyFailedToUnassign', { ns: 'toast' })
			:	t('keysFailedToUnassign', { ns: 'toast' });

		setShowModalLoader(true);
		await organisationService
			.unassignKeys(keyIds)
			.then(() => {
				enqueueSnackbar(successValue, { variant: 'success' });
				cleanUpAfterDialog();
			})
			.catch((_reason) => {
				enqueueSnackbar(failedValue, { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const handleAssignKeys = (
		organisationId: string,
		resourceGroupId: string | undefined,
		closeDialog: () => void
	) => {
		const keyIds =
			selectedKeysContext?.singleKey ?
				[selectedKeysContext?.singleKey.id]
			:	(selectedKeysContext?.selectedKeys?.flatMap((value) => value.id) ?? []);
		const isSingleKey = keyIds.length == 1;
		const successValue =
			isSingleKey ?
				t('keyAssignedSuccesfully', { ns: 'toast' })
			:	t('keysAssignedSuccesfully', { ns: 'toast' });
		const failedValue =
			isSingleKey ?
				t('keyFailedToAssign', { ns: 'toast' })
			:	t('keysFailedToAssign', { ns: 'toast' });

		if (keyIds.length == 0) {
			enqueueSnackbar(failedValue, { variant: 'error' });
		}

		setShowModalLoader(true);
		organisationService
			.assignKeysToOrganisation(keyIds, organisationId)
			.then(() => {
				if (resourceGroupId) {
					resourceGroupService
						.assignKeysToResourceGroup(keyIds, resourceGroupId, organisationId)
						.then(() => {
							cleanUpAfterDialog(closeDialog);
							enqueueSnackbar(successValue, { variant: 'success' });
						})
						.catch((_reason) => {
							enqueueSnackbar(failedValue, { variant: 'error' });
						});
				} else {
					cleanUpAfterDialog(closeDialog);
					enqueueSnackbar(successValue, { variant: 'success' });
				}
			})
			.catch((_reason) => {
				enqueueSnackbar(t('failedToAssignToOrganisation', { ns: 'errors' }), { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const cleanUpAfterDialog = (closeDialog?: () => void) => {
		setUserAction(null);
		userAction?.mutate?.();
		userAction?.clearSelection?.();
		selectedKeysContext.updateSingleKey(undefined);
		closeDialog?.();
	};

	const handleExport = useCallback(
		async (format: ExportFormat) => {
			setExportStatus('exporting');

			try {
				const search = searchParams.get(queryParameters.SearchQuery) ?? undefined;
				const keyType = searchParams.get(queryParameters.KeyType) ?? undefined;
				const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;

				const blob = await exportService.exportKeys({
					format,
					search,
					type: keyType,
					resourceGroupId,
					clientId: organisationId,
				});

				const fileName = `${dayjs().format('DD-MM-YYYY_HH-mm')}_keys_export${getExportFormatExtension(format)}`;
				triggerBlobDownload(blob, fileName);

				enqueueSuccessSnackbar(t('exportGeneratedSuccessfully', { ns: 'toast' }));
			} catch (error) {
				console.error('Export failed:', error);
				enqueueErrorSnackbar(t('exportFailedToGenerate', { ns: 'toast' }));
			} finally {
				clearUserAction();
			}
		},
		[searchParams, organisationId, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]
	);

	return (
		<>
			{organisationId != undefined ?
				<>
					<OrganisationActionBar organisationId={organisationId} showEditButton={false} />
					<KeysDataGrid
						showExportButton={allowsExporting}
						keysListType={'assigned'}
						enableUnassignKey={organisationId != generalInformation.manufacturingOrganisationId}
						enableAssignKey={organisationId == generalInformation.manufacturingOrganisationId}
						overwrittenClientId={organisationId}
						enableMultiSelect
						onAssignKeys={handleAssign}
						onUnassignKeys={handleUnassign}
						onChangeResourceGroupForKeys={handleChangeResourceGroupForKeys}
						showTitle={true}
						showResourceGroupFilter={true}
						handleExportButtonClicked={() => setUserAction({ type: 'export' })}
					/>
				</>
			:	<EmptyState
					icon={'icon/bar-chart-square'}
					title={t('device.status.noHeartbeatsYet', { ns: 'entities' })}
					description='There is no telemetry data available for this device'
				/>
			}
			<KeyMultiAssignDialog
				open={
					userAction?.type === 'assignKeys' || userAction?.type === 'changeResourceGroupForKeys'
				}
				currentlyAssignedOrganisation={
					userAction?.type === 'changeResourceGroupForKeys' ?
						organisationContext.organisation
					:	undefined
				}
				showEditOrganisation={userAction?.type === 'assignKeys'}
				loading={showModalLoader}
				onClose={clearUserAction}
				onFinish={handleAssignKeys}
			/>
			<KeyMultiUnassignDialog
				open={userAction?.type === 'unAssignKeys'}
				loading={showModalLoader}
				onClose={clearUserAction}
				onFinish={handleUnassignKeys}
			/>
			<FormDialog
				open={userAction?.type === 'export'}
				title={
					t('export', { ns: 'actions' }) +
					' ' +
					t('key.labels.plural', { ns: 'entities' }).toLowerCase()
				}
				loading={exportStatus === 'exporting'}
				submitLabel={t('export', { ns: 'actions' })}
			>
				<ExportForm
					status={exportStatus}
					selectedFormat={selectedFormat}
					onFormatChange={setSelectedFormat}
					onSubmit={handleExport}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default OrganisationKeysContainer;
